import {firestoreAction} from "vuexfire";
import moment from "moment";
import {db} from '@/firebase'

const state = {
    repairKit: null,
    repairKits: [],
    toBePicked: 'TO_BE_PICKED',
    picked: 'PICKED',
    packed: 'PACKED',
    shipped: 'SHIPPED',
    technicianReceived: 'TECHNICIAN_RECEIVED',
    toBeReturned: 'TO_BE_RETURNED',
    shippedBack: 'SHIPPED_BACK',
    kitShippedBack: 'SHIPPED_BACK',
    repairKitDetails: [],
    shipmentIdBeingPacked: null,
    shipmentIdBeingReceived: null,
    shipmentIdBeingReturned: null,
};

const getters = {
    repairKit: state => state.repairKit,
    repairKits: state => state.repairKits,

    repairKitsToBePicked: state => state.repairKits.filter(repairKit => repairKit.state_constant === state.toBePicked),
    repairKitsPicked: state => state.repairKits.filter(repairKit => repairKit.state_constant === state.picked),
    repairKitsPacked: state => state.repairKits.filter(repairKit => repairKit.state_constant === state.packed),
    repairKitsShipped: state => state.repairKits.filter(repairKit => repairKit.state_constant === state.shipped),
    repairKitsTechnicianReceived: state => state.repairKits.filter(repairKit => repairKit.state_constant === state.technicianReceived),
    repairKitsToBeReturned: state => state.repairKits.filter(repairKit => repairKit.state_constant === state.toBeReturned),
    repairKitsShippedBack: state => state.repairKits.filter(repairKit => repairKit.state_constant === state.kitShippedBack),

    repairKitDetails: state => state.repairKitDetails,
    detailsPicked: (state) => {
        return state.repairKitDetails.filter(detail => (detail.stock_serial_number_id || detail.picked));
    },
    detailsToBePicked: (state) => {
        return state.repairKitDetails.filter(detail => (!detail.stock_serial_number_id && !detail.picked));
    },

    shipmentIdBeingPacked: state => state.shipmentIdBeingPacked,
    shipmentIdBeingReceived: state => state.shipmentIdBeingReceived,
    shipmentIdBeingReturned: state => state.shipmentIdBeingReturned
};

const actions = {

    /** Load today's repair kits */
    loadTodaysRepairKits: firestoreAction(({bindFirestoreRef}) => {
        return new Promise((resolve, reject) => {
            let date = moment().format('YYYY-MM-DD');
            // date = "2019-11-27";

            bindFirestoreRef(
                'repairKits',
                db.collection('repair_kits')
                    .where('date_created', '==', date)
                ).then(() => {
                    resolve();
                }).catch(() => {
                    reject();
                })
        });
    }),

    /** Load a repair kit */
    loadRepairKit: firestoreAction(({bindFirestoreRef}, {repairKitId}) => {
        repairKitId = repairKitId.toString();

        return bindFirestoreRef(
            'repairKit',
            db.collection('repair_kits').doc(repairKitId)
        )
    }),

    /** Load a repair kit's details */
    loadRepairKitDetails: firestoreAction(({bindFirestoreRef}, {repairKitId}) => {
        return new Promise((resolve, reject) => {
            repairKitId = repairKitId.toString();

            bindFirestoreRef(
                'repairKitDetails',
                db.collection(`repair_kits/${repairKitId}/repair_kit_details`)
                    .orderBy('stock_item_title')
                    ).then(() => {
                        resolve();
                    }).catch(() => {
                        reject();
                    })
        });
    }),

    /** Print repair kit label */
    printRepairKitLabel({commit}, repairKitId) {

        db.collection('repair_kits')
            .doc(repairKitId)
            .update({ label_printed: true })
            .then(() => {
                commit('PRINT_LABEL');
            });

        this.$axios.get(`/api/v4/repair-kits/${repairKitId}/print-label`);
    },

    repairKitDetailBarcodeScanned({dispatch}, barcodeScanned) {
        return new Promise((resolve, reject) => {
            if (barcodeScanned.type === 'serialNumber') {
                return dispatch('addSerialNumber', barcodeScanned)
                    .then(message => {
                        return resolve(message);
                    })
                    .catch(message => {
                        return reject(message);
                    });
            }

            if (barcodeScanned.type === 'asset') {
                return dispatch('addAsset', barcodeScanned)
                    .then(message => {
                        return resolve(message);
                    })
                    .catch(message => {
                        return reject(message);
                    });
            }
            return reject('Unknown barcode type.');
        });
    },

    /** Add serial number */
    addSerialNumber({getters}, barcodeScanned) {
        return new Promise((resolve, reject) => {

            let stockItemRequired = false;

            getters.repairKitDetails.forEach(detail => {
                if (barcodeScanned.stock_id === detail.stock_item_id) {
                    stockItemRequired = true;
                }
            });

            if (!stockItemRequired) {
                reject("This stock item is not part of this kit");
                return;
            }

            let hasErrors = false;

            getters.detailsPicked.forEach(detail => {
                if (barcodeScanned.id === detail.stock_serial_number_id) {
                    reject("Serial number already scanned");
                    hasErrors = true;
                }
            });

            if (hasErrors) {
                return;
            }

            let serialNumberAdded = false;

            getters.detailsToBePicked.forEach(detail => {
                if (serialNumberAdded) {
                    return;
                }

                if (barcodeScanned.stock_id === detail.stock_item_id) {

                    serialNumberAdded = true;

                    db.collection(`repair_kits/${detail.repair_kit_id}/repair_kit_details`)
                        .doc(detail.id)
                        .update({ stock_serial_number_id: barcodeScanned.id })
                        .then(() => {
                            resolve("Serial number added");
                        });
                }
            });

        });

    },

    /** Add serial number */
    addAsset({getters}, barcodeScanned) {
        return new Promise((resolve, reject) => {

            let assetRequired = false;

            getters.repairKitDetails.forEach(detail => {
                if (barcodeScanned.id === detail.asset_id) {
                    assetRequired = true;
                }
            });

            if (!assetRequired) {
                reject("This asset is not part of this kit");
                return;
            }

            let hasErrors = false;

            getters.detailsPicked.forEach(detail => {
                if (barcodeScanned.id === detail.asset_id) {
                    reject("Asset already scanned");
                    hasErrors = true;
                }
            });

            if (hasErrors) {
                return;
            }

            let assetAdded = false;

            getters.detailsToBePicked.forEach(detail => {
                if (assetAdded) {
                    return;
                }

                if (barcodeScanned.id === detail.asset_id) {

                    assetAdded = true;

                    db.collection(`repair_kits/${detail.repair_kit_id}/repair_kit_details`)
                        .doc(detail.id)
                        .update({ picked: true })
                        .then(() => {
                            resolve("Asset added");
                        });
                }
            });
        });
    },

    /** Set the scanned shipment */
    setShipmentScanned({commit}, shipmentId) {
        commit('SET_SHIPMENT_SCANNED', shipmentId);
    },

    /** Clear the scanned shipment */
    clearShipmentScanned({commit}) {
        commit('CLEAR_SHIPMENT_SCANNED');
    },

    /** Load shipment details */
    loadShipmentDetails: firestoreAction(({bindFirestoreRef}, {barcodeScanned}) => {
        return new Promise((resolve, reject) => {
            if (barcodeScanned.type !== 'shipment') {
                return resolve();
            }

            bindFirestoreRef(
                'repairKits',
                db.collection(`repair_kits`)
                    .where('shipment_id', '==', barcodeScanned.id)
                    .orderBy('day_number')
            ).then(() => {
                resolve();
            }).catch(() => {
                reject();
            })
        });
    }),

    /** Set the scanned shipment */
    setReceivedShipmentScanned({commit}, shipmentId) {
        commit('SET_RECEIVED_SHIPMENT_SCANNED', shipmentId);
    },

    /** Clear the scanned shipment */
    clearReceivedShipmentScanned({commit}) {
        commit('CLEAR_RECEIVED_SHIPMENT_SCANNED');
    },

    /** Load inbound shipment details */
    loadReceivedShipmentDetails: firestoreAction(({bindFirestoreRef}, {barcodeScanned}) => {
        return new Promise((resolve, reject) => {
            if (barcodeScanned.type !== 'shipment') {
                return resolve();
            }

            bindFirestoreRef(
                'repairKits',
                db.collection(`repair_kits`)
                    .where('shipment_id', '==', barcodeScanned.id)
                    .where('state_constant', 'in', [state.packed, state.technicianReceived])
            ).then(() => {
                resolve();
            }).catch(() => {
                reject();
            })
        });
    }),

    /** Set the scanned return shipment */
    setReturnShipmentScanned({commit}, shipmentId) {
        commit('SET_RETURN_SHIPMENT_SCANNED', shipmentId);
    },

    /** Clear the scanned return shipment */
    clearReturnShipmentScanned({commit}) {
        commit('CLEAR_RETURN_SHIPMENT_SCANNED');
    },

    /** Load outbound shipment details */
    loadReturnShipmentDetails: firestoreAction(({bindFirestoreRef}, {barcodeScanned}) => {
        return new Promise((resolve, reject) => {
            if (barcodeScanned.type !== 'returnShipment') {
                return resolve();
            }

            bindFirestoreRef(
                'repairKits',
                db.collection(`repair_kits`)
                    .where('return_shipment_id', '==', barcodeScanned.id)
                    .where('state_constant', '==', state.kitShippedBack)
            ).then(() => {
                resolve();
            }).catch(() => {
                reject();
            })
        });
    }),

    /** Change repair kit status to be packed */
    setRepairKitAsPicked({state}, repairKitId) {
        repairKitId = repairKitId.toString();

        db.collection('repair_kits')
            .doc(repairKitId)
            .update({ state_constant: state.picked });
    },

    /** Change repair kit status as packed */
    setRepairKitAsPacked({state}, repairKitId) {
        repairKitId = repairKitId.toString();

        db.collection('repair_kits')
            .doc(repairKitId)
            .update({ state_constant: state.packed });
    },

    /** Change repair kit status technician received  */
    setRepairKitAsTechnicianReceived({state}, repairKitId) {
        return new Promise((resolve, reject) => {
            repairKitId = repairKitId.toString();

            db.collection('repair_kits')
                .doc(repairKitId)
                .update({state_constant: state.technicianReceived})
                .then(() => {
                    resolve();
                }).catch(() => {
                    reject();
                })
        });
    },

    /** Change repair kit status shipped back  */
    setRepairKitAsShippedBack({state}, repairKitId) {
        return new Promise((resolve, reject) => {
            repairKitId = repairKitId.toString();

            db.collection('repair_kits')
                .doc(repairKitId)
                .update({
                    state_constant: state.kitShippedBack,
                    return_shipment_id: state.shipmentIdBeingReturned
                })
                .then(() => {
                    resolve();
                }).catch(() => {
                    reject();
                })
        });
    },

    /** Sync the kit list */
    syncKitList() {
        this.$axios.get(`/api/v4/repair-kits/sync`);
    }

};

const mutations = {
    SET_SHIPMENT_SCANNED(state, shipmentId) {
        state.shipmentIdBeingPacked = shipmentId;
    },

    CLEAR_SHIPMENT_SCANNED(state) {
        state.shipmentIdBeingPacked = null;
    },

    SET_RECEIVED_SHIPMENT_SCANNED(state, shipmentId) {
        state.shipmentIdBeingReceived = shipmentId;
    },

    CLEAR_RECEIVED_SHIPMENT_SCANNED(state) {
        state.shipmentIdBeingReceived = null;
    },

    SET_RETURN_SHIPMENT_SCANNED(state, shipmentId) {
        state.shipmentIdBeingReturned = shipmentId;
    },

    CLEAR_RETURN_SHIPMENT_SCANNED(state) {
        state.shipmentIdBeingReturned = null;
    },

    PRINT_LABEL(state) {
        state.repairKit.label_printed = true;
    }
};

export default {
    state,
    getters,
    actions,
    mutations
}